// @flow

import React from 'react';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import type ExperimentsClient from 'app/common/lib/ExperimentsClient';
import { ExperimentsPropType } from 'app/common/lib/react/withUnauthSeoExperiments';
import { Icon } from 'gestalt';
import { shouldUseVaseCarousel } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import type { ContextType, PinType } from 'app/common/react/components/growth/unauth/lib/PropTypes';

type Props = {
  annotations: Array<string>,
  description: string,
  vaseTexts: Array<string>,
  pin: PinType,
  expanded: boolean,
  seoUnauthExperiments: ExperimentsClient,
  pinGridOptGroup?: string,
};

const styles = {
  newVaseText: {
    fontFamily: 'HelveticaNeue',
    fontSize: '12px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.17',
    letterSpacing: '-0.1px',
    color: '#333333',
    margin: '8px 0',
    display: 'flex',
  },
  hidden: {
    display: 'none',
  },
  vaseComments: {
    fontSize: '14px',
    marginTop: '20px',
  },
};

export const HiddenDescriptions = (props: Props, context: ContextType) => {
  const { annotations, vaseTexts, expanded, seoUnauthExperiments, pinGridOptGroup = '' } = props;
  const inCleanupPinGridStructureGroup = pinGridOptGroup.startsWith('enabled_structure_outline');
  const inPinGridOptStructureGroup = pinGridOptGroup.startsWith('enabled_structure');
  const inSemanticGroup = pinGridOptGroup.startsWith('enabled_clean');
  // For regular users do not include hidden texts in DOM
  if (!expanded && !context.isBot && !inSemanticGroup) {
    return null;
  }

  let textStyle = {};
  let commentsStyle = {};

  if (pinGridOptGroup.startsWith('enabled')) {
    textStyle = expanded || inSemanticGroup ? null : styles.hidden;
    commentsStyle = expanded || inSemanticGroup ? null : styles.hidden;
  } else {
    textStyle = expanded ? styles.newVaseText : { ...styles.newVaseText, ...styles.hidden };
    commentsStyle = expanded ? styles.vaseComments : { ...styles.vaseComments, ...styles.hidden };
  }

  const markupList = [];

  // when carousel is enabled, omit tags/annotations from hidden description
  if (
    !shouldUseVaseCarousel(annotations, seoUnauthExperiments) &&
    annotations &&
    annotations.length > 0
  ) {
    markupList.push(
      <h4
        key="tags"
        data-test-id="vasetags"
        className={pinGridOptGroup.startsWith('enabled') ? 'HiddenDescriptions_vasetags' : ''}
        style={textStyle}
      >
        {annotations.join(', ')}
      </h4>,
    );
  }

  const commentInnerHtml = inCleanupPinGridStructureGroup ? (
    <Icon icon="speech" accessibilityLabel="expand" color="gray" size="12" inline />
  ) : (
    `${i18n._('What others are saying', 'Comments from other people')}`
  );

  const commentsHeader = inPinGridOptStructureGroup ? (
    <div
      key={'vase_others_saying'}
      style={commentsStyle}
      className={pinGridOptGroup.startsWith('enabled') ? 'HiddenDescriptions_vasetags_title' : ''}
    >
      {commentInnerHtml}
    </div>
  ) : (
    <h3
      key={'vase_others_saying'}
      style={commentsStyle}
      className={pinGridOptGroup.startsWith('enabled') ? 'HiddenDescriptions_vasetags_title' : ''}
    >
      {commentInnerHtml}
    </h3>
  );

  if (vaseTexts && vaseTexts.length > 0) {
    if (!inSemanticGroup) {
      markupList.push(commentsHeader);
    }

    markupList.push(
      ...vaseTexts.map((text, index) => (
        <h4
          key={`vase-${index}`}
          data-test-id="vasedesc"
          className={pinGridOptGroup.startsWith('enabled') ? 'HiddenDescriptions_vasetags' : ''}
          style={textStyle}
        >
          {inSemanticGroup && (
            <div style={{ marginRight: '4px' }}>
              <Icon icon="speech" accessibilityLabel="expand" color="gray" size="12" inline />
            </div>
          )}
          {`${text}`}
        </h4>
      )),
    );
  }
  return <React.Fragment>{markupList}</React.Fragment>;
};

HiddenDescriptions.propTypes = {
  annotations: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
  expanded: PropTypes.bool,
  seoUnauthExperiments: ExperimentsPropType,
  vaseTexts: PropTypes.arrayOf(PropTypes.string),
  pinGridOptGroup: PropTypes.string,
  pin: PropTypes.shape({
    board: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    }),
    pinner: PropTypes.shape({
      full_name: PropTypes.string,
    }),
  }),
};

HiddenDescriptions.contextTypes = {
  isBot: PropTypes.bool.isRequired,
  pageType: PropTypes.string.isRequired,
};

export default HiddenDescriptions;
